import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import { useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend,
  Line, LineChart, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

const CHART_COLORS = [
  "#3d8b3d","#2196F3","#FF9800","#E91E63","#9C27B0",
  "#00BCD4","#FF5722","#607D8B","#795548","#4CAF50",
];

export default function Charts() {
  const [showForm, setShowForm] = useState(false);
  const now = new Date();
  const { t, ui, formatCurrency, categoryName } = useI18n();

  const { data: monthlyTotals = [], isLoading: totalsLoading } = trpc.transactions.monthlyTotals.useQuery({ months: 12 });
  const { data: categorySpending = [] } = trpc.transactions.categorySpending.useQuery({
    month: now.getMonth() + 1, year: now.getFullYear(),
  });
  const { data: categories = [] } = trpc.categories.list.useQuery({});

  const lineData = monthlyTotals.map((m, i) => {
    const cumBalance = monthlyTotals.slice(0, i + 1).reduce((acc, cur) => acc + cur.income - cur.expense, 0);
    return { ...m, balance: cumBalance };
  });

  const pieData = categorySpending
    .filter((s) => s.total > 0)
    .map((s) => {
      const cat = categories.find((c) => c.id === s.categoryId);
      return { name: categoryName(cat?.name) || categoryName("Outros"), value: s.total };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  return (
    <BolsoLayout onNewTransaction={() => setShowForm(true)}>
      <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-6">
        <h1 className="text-2xl font-serif font-bold text-foreground">{ui.charts.title}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{ui.charts.incomeVsExpenses12}</CardTitle>
            </CardHeader>
            <CardContent>
              {totalsLoading ? <Skeleton className="h-56" /> : (
                <div role="img" aria-label={ui.charts.incomeVsExpenses12}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthlyTotals} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="income" name={t.dashboard.income} fill="var(--income)" radius={[3,3,0,0]} />
                    <Bar dataKey="expense" name={t.dashboard.expenses} fill="var(--expense)" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{ui.charts.expensesCurrentMonth}</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">{t.common.noData}</div>
              ) : (
                <div role="img" aria-label={ui.charts.expensesCurrentMonth}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{ui.charts.balanceEvolution}</CardTitle>
          </CardHeader>
          <CardContent>
            {totalsLoading ? <Skeleton className="h-48" /> : (
              <div role="img" aria-label={ui.charts.balanceEvolution}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={lineData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Line type="monotone" dataKey="balance" name={t.reports.balance} stroke="var(--primary)" strokeWidth={2.5} dot={{ fill: "var(--primary)", r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category breakdown table */}
        {pieData.length > 0 && (
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{ui.charts.categoryBreakdown}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {pieData.map((item, i) => {
                  const total = pieData.reduce((acc, p) => acc + p.value, 0);
                  const pct = total > 0 ? (item.value / total) * 100 : 0;
                  return (
                    <div key={item.name} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="flex-1 text-sm font-medium text-foreground">{item.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-muted rounded-full h-1.5 hidden sm:block">
                          <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">{pct.toFixed(0)}%</span>
                        <span className="text-sm font-semibold expense-text tabular-nums">{formatCurrency(item.value)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <TransactionForm open={showForm} onClose={() => setShowForm(false)} />
    </BolsoLayout>
  );
}
